const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:3000/api/v1';

const REFRESH_TOKEN_KEY = 'agrida_refresh_token';

// Access token is kept in memory only (never persisted) — an XSS payload that reads
// localStorage still can't steal a live session token, only the longer-lived refresh
// token, which is opaque and single-use-rotated server-side (see IdentityService.refresh).
let accessToken: string | null = null;
let refreshToken: string | null = null;
try {
  refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
} catch {
  // localStorage unavailable (e.g. private mode) — session just won't survive reload
}

let refreshInFlight: Promise<boolean> | null = null;
const sessionListeners = new Set<() => void>();

export function onSessionExpired(listener: () => void): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

function notifySessionExpired() {
  sessionListeners.forEach((l) => l());
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  mfaEnrollmentRequired?: boolean;
}

export function setTokens(pair: TokenPair) {
  accessToken = pair.accessToken;
  refreshToken = pair.refreshToken;
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, pair.refreshToken);
  } catch {
    // ignore
  }
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {
    // ignore
  }
}

export function hasStoredSession(): boolean {
  return Boolean(refreshToken);
}

export function getRefreshToken(): string | null {
  return refreshToken;
}

export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(status: number, body: unknown, message?: string) {
    super(message || (typeof body === 'object' && body && 'message' in body ? String((body as { message?: unknown }).message) : `Yêu cầu thất bại (${status})`));
    this.status = status;
    this.body = body;
  }
}

async function performRefresh(): Promise<boolean> {
  if (!refreshToken) return false;
  const currentRefreshToken = refreshToken;
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: currentRefreshToken }),
    });
    if (!res.ok) {
      clearTokens();
      notifySessionExpired();
      return false;
    }
    const data = (await res.json()) as TokenPair;
    setTokens(data);
    return true;
  } catch {
    return false;
  }
}

function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// Exposed so authStore can proactively restore a session on app boot (accessToken
// lives only in memory, so a page reload always starts with accessToken === null).
export { refreshSession };

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  skipAuthRetry?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (res.status === 401 && !options.skipAuthRetry && refreshToken) {
    const refreshed = await refreshSession();
    if (refreshed) return request<T>(path, { ...options, skipAuthRetry: true });
    notifySessionExpired();
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // non-JSON error body
    }
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  // Raw binary PUT for the file-upload presign flow — the upload URL is a
  // presign-token-guarded path (no JWT needed), and the body is a Blob, not JSON.
  // `contentType` is passed in rather than taken from `blob.type` so the wire
  // header matches the type the file was presigned with — see baseMimeType in
  // api/file.ts for why a MediaRecorder's own `type` must not be sent verbatim.
  uploadRaw: async (path: string, blob: Blob, contentType?: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}${path}`, {
      method: 'PUT',
      headers: { 'Content-Type': contentType || blob.type || 'application/octet-stream' },
      body: blob,
    });
    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // non-JSON error body
      }
      throw new ApiError(res.status, body);
    }
  },
};
